import {z} from 'zod';

import i18n from '@/i18n';
import type {
  ChangeQuantityInput,
  CreateWarehouseInput,
  CreateWarehouseItemInput,
  TransferWarehouseItemInput,
  UpdateWarehouseItemInput,
  Warehouse,
  WarehouseItem,
} from '@/services/graphqlHooks';
import {UserGroup} from '@/services/graphqlHooks';

import {isEmployeesGroupAllowedToUpdateStock} from './warehouseUtils';

/***********************
 * Warehouse
 ***********************/

export const warehouseFormSchema = z.object({
  name: z.string().trim().min(1, i18n.t('Name is required')),
  location: z.string().trim().optional(),
  canEmployeesUpdateStock: z.boolean(),
});

export type WarehouseFormValues = z.infer<typeof warehouseFormSchema>;

export const warehouseFormDefaultValues = (
  warehouse?: Partial<Warehouse>
): WarehouseFormValues => ({
  name: warehouse?.name || '',
  location: warehouse?.location || '',
  canEmployeesUpdateStock: isEmployeesGroupAllowedToUpdateStock(
    warehouse?.allowedGroups as UserGroup[]
  ),
});

const allGroups = Object.values(UserGroup);

export const mapWarehouseValuesToCreateWarehouseInput = (
  value: WarehouseFormValues
): CreateWarehouseInput => ({
  name: value.name,
  location: value.location,
  allowedGroups: value.canEmployeesUpdateStock ? allGroups : [],
});

/***********************
 * Warehouse Item Create
 ***********************/
export const warehouseItemCreateFormSchema = z.object({
  name: z.string().trim().min(1, i18n.t('Name is required')),
  quantity: z.number().min(0, i18n.t('Quantity cannot be negative')),
  warehouseId: z.string().min(1, i18n.t('Warehouse ID is required')),
});

export type WarehouseItemCreateFormValues = z.infer<
  typeof warehouseItemCreateFormSchema
>;

export const warehouseItemCreateFormDefaultValues = (
  warehouseItem?: Partial<WarehouseItem>
): WarehouseItemCreateFormValues => ({
  name: warehouseItem?.name || '',
  quantity: warehouseItem?.quantity || 0,
  warehouseId: warehouseItem?.warehouseId || '',
});

export const mapWarehouseItemCreateValuesToCreateWarehouseItemInput = (
  value: WarehouseItemCreateFormValues
): CreateWarehouseItemInput => ({
  name: value.name,
  quantity: value.quantity,
  warehouseId: value.warehouseId,
});

/***********************
 * Warehouse Item Update
 ***********************/

export const warehouseItemUpdateFormSchema = z.object({
  name: z.string().trim().min(1, i18n.t('Name is required')),
  warehouseId: z.string().min(1, i18n.t('Warehouse ID is required')),
});

export type WarehouseItemUpdateFormValues = z.infer<
  typeof warehouseItemUpdateFormSchema
>;

export const warehouseItemUpdateFormDefaultValues = (
  warehouseItem?: Partial<WarehouseItem>
): WarehouseItemUpdateFormValues => ({
  name: warehouseItem?.name || '',
  warehouseId: warehouseItem?.warehouseId || '',
});

export const mapWarehouseItemUpdateValuesToUpdateWarehouseItemInput = (
  id: string,
  value: WarehouseItemUpdateFormValues
): UpdateWarehouseItemInput => ({
  id,
  name: value.name,
  warehouseId: value.warehouseId,
});

/***********************
 * Warehouse Item Stcok Update
 ***********************/
export const warehouseItemStockUpdateFormSchema = z.object({
  id: z.string(),
  amount: z.number(),
  warehouseId: z.string().min(1, i18n.t('Warehouse ID is required')),
});

export type WarehouseItemStockUpdateFormValues = z.infer<
  typeof warehouseItemStockUpdateFormSchema
>;

export const warehouseItemStockUpdateFormDefaultValues = (
  warehouseItem?: Partial<WarehouseItem>
): WarehouseItemStockUpdateFormValues => ({
  id: warehouseItem?.id || '',
  amount: 0,
  warehouseId: warehouseItem?.warehouseId || '',
});

export const mapWarehouseItemStockUpdateValuesToChangeQuantityInput = (
  value: WarehouseItemStockUpdateFormValues
): ChangeQuantityInput => ({
  id: value.id,
  amount: Math.abs(value.amount || 0),
  warehouseId: value.warehouseId,
});

/***********************
 * Warehouse Item Transfer
 ***********************/

export const warehouseItemTransferFormSchema = (availableQuantity: number) =>
  z.object({
    id: z.string().min(1, i18n.t('Product is required')),
    warehouseId: z.string().min(1, i18n.t('Warehouse ID is required')),
    targetWarehouseId: z
      .string()
      .min(1, i18n.t('Target warehouse is required')),
    amount: z
      .number()
      .min(1, i18n.t('Quantity must be at least 1'))
      .max(availableQuantity, i18n.t('Quantity exceeds available stock')),
  });

export type WarehouseItemTransferFormValues = z.infer<
  ReturnType<typeof warehouseItemTransferFormSchema>
>;

export const warehouseItemTransferFormDefaultValues = (
  warehouseItem?: Partial<WarehouseItem>
): WarehouseItemTransferFormValues => ({
  id: warehouseItem?.id || '',
  warehouseId: warehouseItem?.warehouseId || '',
  targetWarehouseId: '',
  amount: 1,
});

export const mapWarehouseItemTransferValuesToTransferWarehouseItemInput = (
  value: WarehouseItemTransferFormValues
): TransferWarehouseItemInput => ({
  id: value.id,
  warehouseId: value.warehouseId,
  targetWarehouseId: value.targetWarehouseId,
  amount: value.amount,
});
